import { DEFAULT_PAGE_SIZE } from '@/shared/lib/constants';

import type {
  Connector,
  ConnectorField,
  ConnectorListParams,
  ConnectorListResponse,
  ConnectorRunningStatus,
  ConnectorType,
  ConnectorTypeSchema,
  ConnectorTypesResponse,
  CreateConnectorRequest,
  DeleteConnectorsRequest,
  UpdateConnectorRequest,
  UpdateConnectorsStatusRequest,
} from '@/entities/connector/types';
import {
  CONNECTOR_RUNNING_STATUSES,
  CONNECTOR_STATUS_FILTER_VALUES,
  CONNECTOR_STATUSES,
} from '@/entities/connector/types';

export const mockConnectorTypes: ConnectorType[] = [
  { type: 'opc_ua', displayName: 'OPC-UA Source' },
  { type: 'grpc', displayName: 'gRPC Source' },
  { type: 'mssql_jdbc', displayName: 'MSSQL JDBC Source' },
  { type: 'mssql_cdc', displayName: 'MSSQL CDC Source' },
  { type: 'influxdb', displayName: 'InfluxDB Source' },
  { type: 'http', displayName: 'HTTP Source' },
  { type: 'http_jwt', displayName: 'HTTP JWT Source' },
  { type: 'sftp', displayName: 'SFTP Source' },
  { type: 'postgresql_jdbc', displayName: 'PostgreSQL JDBC Source' },
  { type: 'postgresql_cdc', displayName: 'PostgreSQL CDC Source' },
  { type: 'mysql_jdbc', displayName: 'MySQL JDBC Source' },
  { type: 'mysql_cdc', displayName: 'MySQL CDC Source' },
  { type: 'mqtt', displayName: 'MQTT Source' },
  { type: 'minio_s3', displayName: 'MinIO S3 Source' },
  { type: 'ceph_s3', displayName: 'Ceph S3 Source' },
  { type: 'mongodb_jdbc', displayName: 'MongoDB JDBC Source' },
  { type: 'mongodb_cdc', displayName: 'MongoDB CDC Source' },
  { type: 'oracle_jdbc', displayName: 'Oracle JDBC Source' },
  { type: 'oracle_cdc', displayName: 'Oracle CDC Source' },
];

const connectorTypeDisplayNames = Object.fromEntries(
  mockConnectorTypes.map((connectorType) => [connectorType.type, connectorType.displayName])
);

const field = (
  label: string,
  inputType: ConnectorField['inputType'],
  required = true,
  validationRules?: ConnectorField['validationRules']
): ConnectorField => ({
  label,
  inputType,
  required,
  placeholder: `${label}을(를) 입력해주세요`,
  validationRules,
});

const endpointField = field('endpoint', 'text', true);
const intervalField = field('interval_ms', 'number', true, { min: 100, max: 3_600_000 });
const usernameField = field('username', 'text', true);
const passwordField = field('password', 'password', false);
const tablesField = field('tables', 'text', true);
const databaseField = field('database', 'text', true);
const hostnameField = field('hostname', 'text', true);
const portField = field('port', 'number', true, { min: 1, max: 65_535 });

const connectorTypeSchemaFields: Partial<Record<string, ConnectorField[]>> = {
  opc_ua: [endpointField, intervalField, field('nodes', 'textarea', true)],
  grpc: [endpointField, intervalField, field('service_name', 'text', true), field('method_name', 'text', true)],
  mssql_jdbc: [endpointField, intervalField, tablesField, usernameField, passwordField],
  mssql_cdc: [databaseField, hostnameField, portField, tablesField, usernameField, passwordField],
  influxdb: [
    endpointField,
    intervalField,
    field('bucket', 'text', true),
    field('org', 'text', true),
    field('token', 'password', false),
    field('query', 'textarea', true),
  ],
  http: [endpointField, intervalField],
  http_jwt: [
    endpointField,
    intervalField,
    field('auth_url', 'text', false),
    field('refresh_url', 'text', false),
    field('username', 'text', false),
    passwordField,
  ],
  sftp: [intervalField, field('directory', 'text', true), hostnameField, portField, usernameField, passwordField],
  postgresql_jdbc: [endpointField, intervalField, tablesField, usernameField, passwordField],
  postgresql_cdc: [databaseField, hostnameField, portField, tablesField, usernameField, passwordField],
  mysql_jdbc: [endpointField, intervalField, tablesField, usernameField, passwordField],
  mysql_cdc: [
    databaseField,
    hostnameField,
    portField,
    field('server_id', 'number', false),
    tablesField,
    usernameField,
    passwordField,
  ],
  mqtt: [endpointField, field('mqtt_topic', 'text', true)],
  minio_s3: [
    endpointField,
    intervalField,
    field('access_key', 'text', true),
    field('secret_key', 'password', false),
    field('bucket', 'text', true),
  ],
  ceph_s3: [
    endpointField,
    intervalField,
    field('access_key', 'text', true),
    field('secret_key', 'password', false),
    field('bucket', 'text', true),
  ],
  mongodb_jdbc: [
    endpointField,
    intervalField,
    field('collections', 'text', true),
    field('username', 'text', false),
    passwordField,
  ],
  mongodb_cdc: [field('connection_string', 'text', true), databaseField, field('collections', 'text', true)],
  oracle_jdbc: [endpointField, intervalField, tablesField, usernameField, passwordField],
  oracle_cdc: [databaseField, hostnameField, portField, tablesField, usernameField, passwordField],
};

const createDataInfo = (
  site: string,
  area: string,
  process: string,
  unit: string,
  component: string,
  dataName: string
): Connector['dataInfo'] => ({
  enterprise: 'onepredict',
  site,
  area,
  process,
  unit,
  component,
  dataName,
});

const createWorker = (index: number): Connector['worker'] => ({
  id: `worker-${String(index).padStart(3, '0')}`,
  name: `worker-${String(index).padStart(2, '0')}`,
});

const initialConnectors: Connector[] = [
  {
    id: 'conn-001',
    type: 'opc_ua',
    name: 'OPC-UA GSP ECMS1 Waveform Collector',
    config: {
      endpoint: 'opc.tcp://gsp-ecms1-edge.local:4840',
      interval_ms: 1000,
      nodes: 'ns=2;s=GSP.ECMS1.Chart.X,ns=2;s=GSP.ECMS1.Chart.Y,ns=2;s=GSP.ECMS1.Trend.RPM',
    },
    worker: createWorker(1),
    isActive: true,
    status: CONNECTOR_STATUSES.SUCCESS,
    dataInfo: createDataInfo('gsp', 'ecms1', 'waveform', 'turbine', 'sensor', 'gsp_ecms1_chart'),
  },
  {
    id: 'conn-002',
    type: 'mssql_jdbc',
    name: 'MSSQL JDBC GSP Bearing Master Sync',
    config: {
      endpoint: 'jdbc:sqlserver://gsp-master-db.local:1433;databaseName=g_turbo',
      interval_ms: 300000,
      tables: 'dbo.gsp_ecms1_bearings1,dbo.gsp_ecms1_bearings2',
      username: 'gsp_master_reader',
      password: '',
    },
    worker: createWorker(2),
    isActive: true,
    status: CONNECTOR_STATUSES.SUCCESS,
    dataInfo: createDataInfo('gsp', 'ecms1', 'master', 'turbine', 'bearing', 'gsp_ecms1_bearings'),
  },
  {
    id: 'conn-003',
    type: 'mqtt',
    name: 'MQTT GSP DCS Telemetry Collector',
    config: {
      endpoint: 'tcp://gsp-dcs-broker.local:1883',
      mqtt_topic: 'plant/gsp/dcs/telemetry/#',
    },
    worker: createWorker(3),
    isActive: true,
    status: CONNECTOR_STATUSES.SUCCESS,
    dataInfo: createDataInfo('gsp', 'dcs', 'telemetry', 'plant', 'broker', 'gsp_dcs'),
  },
  {
    id: 'conn-004',
    type: 'influxdb',
    name: 'InfluxDB KMP Waveform Historian',
    config: {
      endpoint: 'http://kmp-influxdb.local:8086',
      interval_ms: 5000,
      bucket: 'kmp-waveform',
      org: 'onepredict',
      token: '',
      query: 'from(bucket: "kmp-waveform") |> range(start: -10m) |> filter(fn: (r) => r._measurement == "kmp_ecms")',
    },
    worker: createWorker(4),
    isActive: true,
    status: CONNECTOR_STATUSES.SUCCESS,
    dataInfo: createDataInfo('kmp', 'ecms', 'waveform', 'turbine', 'historian', 'kmp_ecms_chart'),
  },
  {
    id: 'conn-005',
    type: 'postgresql_jdbc',
    name: 'PostgreSQL JDBC KMP Bearing Master Sync',
    config: {
      endpoint: 'jdbc:postgresql://kmp-meta-db.local:5432/g_turbo',
      interval_ms: 600000,
      tables: 'public.kmp_ecms_bearings1',
      username: 'kmp_meta_reader',
      password: '',
    },
    isActive: false,
    status: CONNECTOR_STATUSES.SUCCESS,
    dataInfo: createDataInfo('kmp', 'ecms', 'master', 'turbine', 'bearing', 'kmp_ecms_bearings1'),
  },
  {
    id: 'conn-006',
    type: 'http_jwt',
    name: 'HTTP JWT Hanwha Impact Waveform API',
    config: {
      endpoint: 'https://hanwha-impact-api.local/api/v1/waveforms',
      interval_ms: 60000,
      auth_url: 'https://hanwha-impact-api.local/auth/login',
      refresh_url: 'https://hanwha-impact-api.local/auth/refresh',
      username: 'hanwha_waveform_sync',
      password: '',
    },
    worker: createWorker(6),
    isActive: true,
    status: CONNECTOR_STATUSES.FAILURE,
    dataInfo: createDataInfo('hanwha', 'impact', 'waveform', 'compressor', 'api', 'hanwha_impact_chart'),
  },
  {
    id: 'conn-007',
    type: 'sftp',
    name: 'SFTP Hanwha Bearing Master Upload',
    config: {
      interval_ms: 900000,
      directory: '/incoming/hanwha/bearing-master',
      hostname: 'hanwha-sftp.local',
      port: 22,
      username: 'hanwha_master_sync',
      password: '',
    },
    worker: createWorker(7),
    isActive: true,
    status: CONNECTOR_STATUSES.SUCCESS,
    dataInfo: createDataInfo('hanwha', 'impact', 'master', 'compressor', 'bearing', 'hanwha_impact_bearings1'),
  },
  {
    id: 'conn-008',
    type: 'mysql_jdbc',
    name: 'MySQL JDBC GSC Bearing Reference Sync',
    config: {
      endpoint: 'jdbc:mysql://gsc-meta-db.local:3306/g_turbo',
      interval_ms: 1800000,
      tables: 'gsc_bearings',
      username: 'gsc_reference_reader',
      password: '',
    },
    isActive: false,
    status: CONNECTOR_STATUSES.SUCCESS,
    dataInfo: createDataInfo('gsc', 'reference', 'master', 'turbine', 'bearing', 'gsc_bearings'),
  },
];

const cloneConnector = (connector: Connector): Connector => JSON.parse(JSON.stringify(connector)) as Connector;
const cloneConnectors = (connectors: readonly Connector[]): Connector[] => connectors.map(cloneConnector);

let connectorSequence = initialConnectors.length + 1;
let connectorStore: Connector[] = cloneConnectors(initialConnectors);

const normalizeStatusFilter = (status?: ConnectorListParams['status']): ConnectorListParams['status'] => {
  if (!status) return undefined;
  return Array.isArray(status) ? status : [status];
};

const matchesStatusFilter = (connector: Connector, status?: ConnectorListParams['status']): boolean => {
  const statuses = normalizeStatusFilter(status);
  if (!statuses || statuses.length === 0) return true;

  const matchesInactive = statuses.includes(CONNECTOR_STATUS_FILTER_VALUES.EMPTY) && !connector.isActive;
  const matchesActiveStatus = connector.isActive && connector.status !== null && statuses.includes(connector.status);

  return matchesInactive || matchesActiveStatus;
};

const sortConnectors = (
  connectors: readonly Connector[],
  sortBy: ConnectorListParams['sortBy'],
  sortOrder: ConnectorListParams['sortOrder']
): Connector[] => {
  if (!sortBy) return [...connectors];

  const order = sortOrder === 'desc' ? -1 : 1;

  return [...connectors].sort((left, right) => {
    if (sortBy === 'name') {
      return left.name.localeCompare(right.name) * order;
    }

    if (sortBy === 'isActive') {
      return (Number(left.isActive) - Number(right.isActive)) * order;
    }

    const leftStatus = left.isActive ? (left.status ?? '') : CONNECTOR_STATUS_FILTER_VALUES.EMPTY;
    const rightStatus = right.isActive ? (right.status ?? '') : CONNECTOR_STATUS_FILTER_VALUES.EMPTY;
    return leftStatus.localeCompare(rightStatus) * order;
  });
};

const createConnectorId = (): string => `conn-${String(connectorSequence++).padStart(3, '0')}`;

export const getConnectorTypesMockData = (): ConnectorTypesResponse => ({
  items: [...mockConnectorTypes],
});

export const getConnectorTypeSchemaMockData = (type = 'opc_ua'): ConnectorTypeSchema => ({
  type,
  displayName: connectorTypeDisplayNames[type] ?? type,
  version: '1.0.0',
  fields: connectorTypeSchemaFields[type] ?? [],
});

export const getConnectorMockData = (id: string): Connector | undefined => {
  const connector = connectorStore.find((item) => item.id === id);
  return connector ? cloneConnector(connector) : undefined;
};

export const getConnectorsMockData = (params?: ConnectorListParams): ConnectorListResponse => {
  const allItems = connectorStore;
  const search = params?.search?.trim().toLowerCase();

  const filteredItems = sortConnectors(
    allItems.filter((connector) => {
      if (search && !connector.name.toLowerCase().includes(search)) {
        return false;
      }

      if (params?.isActive !== undefined && connector.isActive !== params.isActive) {
        return false;
      }

      return matchesStatusFilter(connector, params?.status);
    }),
    params?.sortBy,
    params?.sortOrder
  );

  const page = params?.page ?? 1;
  const pageSize = params?.pageSize ?? DEFAULT_PAGE_SIZE;
  const startIndex = (page - 1) * pageSize;
  const paginatedItems = filteredItems.slice(startIndex, startIndex + pageSize);
  const activeItems = allItems.filter((connector) => connector.isActive);
  const successCount = activeItems.filter((connector) => connector.status === CONNECTOR_STATUSES.SUCCESS).length;

  return {
    items: cloneConnectors(paginatedItems),
    page: {
      total: filteredItems.length,
      page,
      pageSize,
    },
    summary: {
      enable: activeItems.length,
      disable: allItems.length - activeItems.length,
      success: successCount,
      failure: activeItems.length - successCount,
    },
  };
};

export const createConnectorMockData = (request: CreateConnectorRequest): Connector => {
  const newConnector: Connector = {
    id: createConnectorId(),
    type: request.type,
    name: request.name,
    config: { ...request.config },
    isActive: false,
    status: null,
    dataInfo: { ...request.dataInfo },
  };

  connectorStore.push(newConnector);
  return cloneConnector(newConnector);
};

export const updateConnectorMockData = (id: string, request: UpdateConnectorRequest): Connector | undefined => {
  const connectorIndex = connectorStore.findIndex((connector) => connector.id === id);
  if (connectorIndex === -1) return undefined;

  connectorStore[connectorIndex] = {
    ...connectorStore[connectorIndex],
    type: request.type,
    name: request.name,
    config: { ...request.config },
    dataInfo: { ...request.dataInfo },
  };

  return cloneConnector(connectorStore[connectorIndex]);
};

export const updateConnectorsStatusMockData = (request: UpdateConnectorsStatusRequest): void => {
  const isActive = request.status === CONNECTOR_RUNNING_STATUSES.RUNNING;

  connectorStore = connectorStore.map((connector) => {
    if (!request.ids.includes(connector.id)) return connector;

    const nextWorker =
      isActive && !connector.worker
        ? createWorker(Number(connector.id.replace(/\D/g, '')) || connectorSequence)
        : connector.worker;
    const nextStatus: ConnectorRunningStatus = request.status;

    return {
      ...connector,
      worker: isActive ? nextWorker : undefined,
      isActive,
      status: isActive && connector.status === null ? CONNECTOR_STATUSES.SUCCESS : connector.status,
      config: {
        ...connector.config,
        runningStatus: nextStatus,
      },
    };
  });
};

export const deleteConnectorsMockData = (request: DeleteConnectorsRequest): void => {
  connectorStore = connectorStore.filter((connector) => !request.ids.includes(connector.id));
};

export const resetConnectorMockStore = (): void => {
  connectorSequence = initialConnectors.length + 1;
  connectorStore = cloneConnectors(initialConnectors);
};
